import React, { useState, useCallback, useMemo } from 'react';
import {
  Box, Typography, Button, Stack, Paper, TextField, Divider, Chip,
  IconButton, Tooltip, Alert, useTheme,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SEO, { createSoftwareAppSchema } from '../../../components/SEO';

const DEFAULT_SIGNALS = [
  { name: 'Vehicle Speed', startBit: 0, bitLength: 16, factor: 0.01, offset: 0, unit: 'km/h', isSigned: false },
  { name: 'Engine RPM', startBit: 16, bitLength: 16, factor: 0.25, offset: 0, unit: 'rpm', isSigned: false },
  { name: 'Throttle Position', startBit: 32, bitLength: 8, factor: 0.392157, offset: 0, unit: '%', isSigned: false },
  { name: 'Engine Temp', startBit: 40, bitLength: 8, factor: 1, offset: -40, unit: '°C', isSigned: false },
];

const PRESETS = {
  'OBD-II Speed/RPM': [
    { name: 'Vehicle Speed', startBit: 24, bitLength: 8, factor: 1, offset: 0, unit: 'km/h', isSigned: false },
    { name: 'Engine RPM', startBit: 16, bitLength: 16, factor: 0.25, offset: 0, unit: 'rpm', isSigned: false },
  ],
  'J1939 EEC1': [
    { name: 'Engine Torque Mode', startBit: 0, bitLength: 4, factor: 1, offset: 0, unit: '', isSigned: false },
    { name: 'Driver Demand Torque', startBit: 8, bitLength: 8, factor: 1, offset: -125, unit: '%', isSigned: false },
    { name: 'Actual Engine Torque', startBit: 16, bitLength: 8, factor: 1, offset: -125, unit: '%', isSigned: false },
    { name: 'Engine Speed', startBit: 24, bitLength: 16, factor: 0.125, offset: 0, unit: 'rpm', isSigned: false },
  ],
  'J1939 CCVS': [
    { name: 'Wheel Speed', startBit: 8, bitLength: 16, factor: 0.00390625, offset: 0, unit: 'km/h', isSigned: false },
    { name: 'Cruise Control Active', startBit: 24, bitLength: 2, factor: 1, offset: 0, unit: '', isSigned: false },
    { name: 'Brake Switch', startBit: 26, bitLength: 2, factor: 1, offset: 0, unit: '', isSigned: false },
    { name: 'Clutch Switch', startBit: 28, bitLength: 2, factor: 1, offset: 0, unit: '', isSigned: false },
    { name: 'Park Brake', startBit: 30, bitLength: 2, factor: 1, offset: 0, unit: '', isSigned: false },
  ],
};

function hexToBinaryArray(hexStr) {
  const clean = hexStr.replace(/[\s0x]/gi, '');
  const bits = [];
  for (let i = 0; i < clean.length; i++) {
    const nibble = parseInt(clean[i], 16);
    if (isNaN(nibble)) return null;
    for (let b = 3; b >= 0; b--) {
      bits.push((nibble >> b) & 1);
    }
  }
  return bits;
}

function extractSignalValue(bits, startBit, bitLength, factor, offset, isSigned) {
  if (startBit + bitLength > bits.length) return { raw: null, physical: null, error: 'Out of range' };
  let raw = 0;
  for (let i = 0; i < bitLength; i++) {
    raw = (raw << 1) | bits[startBit + i];
  }
  if (isSigned && bitLength > 0 && (raw & (1 << (bitLength - 1)))) {
    raw -= (1 << bitLength);
  }
  const physical = raw * factor + offset;
  return { raw, physical, error: null };
}

function CanPayloadDecoder() {
  const theme = useTheme();
  const [payload, setPayload] = useState('00 64 10 00 80 5A 00 00');
  const [signals, setSignals] = useState(DEFAULT_SIGNALS);
  const [batchPayloads, setBatchPayloads] = useState('');
  const [showBatch, setShowBatch] = useState(false);

  const addSignal = () => {
    setSignals([...signals, { name: `Signal_${signals.length}`, startBit: 0, bitLength: 8, factor: 1, offset: 0, unit: '', isSigned: false }]);
  };

  const removeSignal = (idx) => {
    setSignals(signals.filter((_, i) => i !== idx));
  };

  const updateSignal = (idx, field, value) => {
    const updated = [...signals];
    updated[idx] = { ...updated[idx], [field]: field === 'name' || field === 'unit' ? value : (field === 'isSigned' ? value : Number(value)) };
    setSignals(updated);
  };

  const loadPreset = (presetName) => {
    setSignals([...PRESETS[presetName]]);
  };

  const decodedValues = useMemo(() => {
    const bits = hexToBinaryArray(payload);
    if (!bits) return signals.map(() => ({ raw: null, physical: null, error: 'Invalid hex' }));
    return signals.map(s => extractSignalValue(bits, s.startBit, s.bitLength, s.factor, s.offset, s.isSigned));
  }, [payload, signals]);

  const binaryView = useMemo(() => {
    const bits = hexToBinaryArray(payload);
    if (!bits) return null;
    return bits;
  }, [payload]);

  const batchResults = useMemo(() => {
    if (!showBatch || !batchPayloads.trim()) return [];
    const lines = batchPayloads.split('\n').filter(l => l.trim());
    return lines.map((line, lineIdx) => {
      const bits = hexToBinaryArray(line);
      if (!bits) return { line: lineIdx + 1, payload: line, error: 'Invalid hex', values: [] };
      const values = signals.map(s => {
        const result = extractSignalValue(bits, s.startBit, s.bitLength, s.factor, s.offset, s.isSigned);
        return { name: s.name, unit: s.unit, ...result };
      });
      return { line: lineIdx + 1, payload: line, error: null, values };
    });
  }, [batchPayloads, signals, showBatch]);

  const handleCopyResults = () => {
    const header = ['Payload', ...signals.map(s => `${s.name} (${s.unit})`)].join(',');
    const rows = batchResults.map(r => {
      if (r.error) return `${r.payload},ERROR`;
      return [r.payload, ...r.values.map(v => v.physical?.toFixed(4) ?? 'ERR')].join(',');
    });
    navigator.clipboard.writeText([header, ...rows].join('\n'));
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'auto', p: 2 }}>
      <SEO
        title="CAN Payload Decoder | Parse Telematics Hex | UmbrellaCo"
        description="Decode vehicle CAN bus payloads from hex strings. Free online telematics tool to extract speed, RPM, and other signals using custom bit definitions."
        keywords="can bus decoder, hex payload parser, telematics can data, decode vehicle signals, j1939 parser"
        url="/spatial-tools/can-decoder"
        jsonLd={createSoftwareAppSchema(
          'CAN Payload Decoder',
          'Decode vehicle CAN bus payloads from hex strings into physical values.',
          'https://umbrellaco.in/spatial-tools/can-decoder',
          ['CAN Bus Decoding', 'Hex Parsing', 'Telematics Analysis']
        )}
      />
      <Box sx={{ mb: 2 }}>
        <Typography component="h1" variant="h6" fontWeight={700}>CAN Payload Decoder</Typography>
        <Typography variant="body2" color="text.secondary">
          Translate raw hexadecimal CAN bus payloads into human-readable physical values using custom signal definitions and presets.
        </Typography>
      </Box>
      {/* Payload Input */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="subtitle2" fontWeight={700}>CAN Payload (Hex)</Typography>
          <Stack direction="row" spacing={0.5}>
            {Object.keys(PRESETS).map(name => (
              <Chip key={name} label={name} size="small" onClick={() => loadPreset(name)}
                sx={{ fontSize: '0.65rem', cursor: 'pointer', '&:hover': { bgcolor: 'primary.main', color: '#fff' } }} />
            ))}
          </Stack>
        </Stack>
        <TextField
          fullWidth size="small" value={payload}
          onChange={(e) => setPayload(e.target.value)}
          placeholder="00 64 10 00 80 5A 00 00"
          sx={{ '& input': { fontFamily: 'monospace', fontSize: '1rem', letterSpacing: '2px' } }}
        />
        {binaryView && (
          <Box sx={{ mt: 1, fontFamily: 'monospace', fontSize: '0.7rem', display: 'flex', flexWrap: 'wrap', gap: '1px' }}>
            {binaryView.map((bit, i) => (
              <Tooltip key={i} title={`Bit ${i}`}>
                <Box component="span" sx={{
                  px: 0.3, py: 0.1, borderRadius: 0.5,
                  bgcolor: signals.some(s => i >= s.startBit && i < s.startBit + s.bitLength)
                    ? 'primary.main' : 'action.hover',
                  color: signals.some(s => i >= s.startBit && i < s.startBit + s.bitLength)
                    ? '#fff' : 'text.secondary',
                  borderRight: (i + 1) % 8 === 0 ? `2px solid ${theme.palette.divider}` : 'none',
                }}>
                  {bit}
                </Box>
              </Tooltip>
            ))}
          </Box>
        )}
      </Paper>

      {/* Decoded Values */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          <PlayArrowIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
          Decoded Values
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 1 }}>
          {signals.map((s, i) => {
            const v = decodedValues[i];
            return (
              <Paper key={i} variant="outlined" sx={{
                p: 1.5, textAlign: 'center',
                borderColor: v.error ? 'error.main' : 'divider',
                background: theme.palette.mode === 'dark' ? 'rgba(99,102,241,0.06)' : 'rgba(99,102,241,0.03)',
              }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>{s.name}</Typography>
                <Typography variant="h6" fontWeight={700} sx={{ fontFamily: 'monospace', color: v.error ? 'error.main' : 'text.primary' }}>
                  {v.error ? 'ERR' : v.physical?.toFixed(2)}
                </Typography>
                <Typography variant="caption" color="text.secondary">{s.unit} (raw: {v.raw ?? '—'})</Typography>
              </Paper>
            );
          })}
        </Box>
      </Paper>

      {/* Signal Definitions */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle2" fontWeight={700}>Signal Definitions</Typography>
          <Button size="small" startIcon={<AddIcon />} onClick={addSignal}>Add Signal</Button>
        </Stack>
        <TableContainer sx={{ maxHeight: '35vh' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {['Name', 'Start Bit', 'Length', 'Factor', 'Offset', 'Unit', 'Signed', ''].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.7rem', py: 0.5 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {signals.map((s, i) => (
                <TableRow key={i} sx={{ '& td': { py: 0.3 } }}>
                  <TableCell>
                    <TextField size="small" value={s.name} onChange={(e) => updateSignal(i, 'name', e.target.value)}
                      sx={{ '& input': { fontSize: '0.75rem', py: 0.5 } }} variant="standard" />
                  </TableCell>
                  <TableCell>
                    <TextField size="small" type="number" value={s.startBit} onChange={(e) => updateSignal(i, 'startBit', e.target.value)}
                      sx={{ width: 55, '& input': { fontSize: '0.75rem', py: 0.5 } }} variant="standard" />
                  </TableCell>
                  <TableCell>
                    <TextField size="small" type="number" value={s.bitLength} onChange={(e) => updateSignal(i, 'bitLength', e.target.value)}
                      sx={{ width: 50, '& input': { fontSize: '0.75rem', py: 0.5 } }} variant="standard" />
                  </TableCell>
                  <TableCell>
                    <TextField size="small" type="number" value={s.factor} onChange={(e) => updateSignal(i, 'factor', e.target.value)}
                      sx={{ width: 65, '& input': { fontSize: '0.75rem', py: 0.5 } }} variant="standard" inputProps={{ step: 0.01 }} />
                  </TableCell>
                  <TableCell>
                    <TextField size="small" type="number" value={s.offset} onChange={(e) => updateSignal(i, 'offset', e.target.value)}
                      sx={{ width: 55, '& input': { fontSize: '0.75rem', py: 0.5 } }} variant="standard" />
                  </TableCell>
                  <TableCell>
                    <TextField size="small" value={s.unit} onChange={(e) => updateSignal(i, 'unit', e.target.value)}
                      sx={{ width: 50, '& input': { fontSize: '0.75rem', py: 0.5 } }} variant="standard" />
                  </TableCell>
                  <TableCell>
                    <input type="checkbox" checked={s.isSigned} onChange={(e) => updateSignal(i, 'isSigned', e.target.checked)} />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => removeSignal(i)}><DeleteIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Batch Decode */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle2" fontWeight={700}>Batch Decode</Typography>
          <Stack direction="row" spacing={1}>
            <Button size="small" onClick={() => setShowBatch(!showBatch)}>
              {showBatch ? 'Hide' : 'Show'}
            </Button>
            {batchResults.length > 0 && (
              <Button size="small" startIcon={<ContentCopyIcon />} onClick={handleCopyResults}>Copy CSV</Button>
            )}
          </Stack>
        </Stack>
        {showBatch && (
          <>
            <TextField multiline rows={4} fullWidth size="small" value={batchPayloads}
              onChange={(e) => setBatchPayloads(e.target.value)}
              placeholder="One hex payload per line..."
              sx={{ mb: 1, '& textarea': { fontFamily: 'monospace', fontSize: '0.75rem' } }}
            />
            {batchResults.length > 0 && (
              <TableContainer sx={{ maxHeight: '30vh' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem' }}>#</TableCell>
                      {signals.map((s, i) => (
                        <TableCell key={i} sx={{ fontWeight: 700, fontSize: '0.7rem' }}>{s.name}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {batchResults.map((r, i) => (
                      <TableRow key={i}>
                        <TableCell sx={{ fontSize: '0.7rem' }}>{r.line}</TableCell>
                        {r.error ? (
                          <TableCell colSpan={signals.length} sx={{ color: 'error.main', fontSize: '0.7rem' }}>{r.error}</TableCell>
                        ) : (
                          r.values.map((v, j) => (
                            <TableCell key={j} sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                              {v.physical?.toFixed(2)} {v.unit}
                            </TableCell>
                          ))
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
}

export default CanPayloadDecoder;
